import React, { useEffect, useState } from "react";
import unsplash from "../api/unsplash";
import LinearProgress from "@mui/material/LinearProgress";
import GlobalSnackbar from "./Snackbar";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Alert,
  Tooltip,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  ButtonGroup,
  DialogActions,
  DialogContentText,
  IconButton,
  Zoom,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import EditIcon from "@mui/icons-material/Edit";
import { Add, Delete, Close, Check, Download } from "@mui/icons-material";
import * as XLSX from "xlsx";
import {
  DataGridPro,
  itIT,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "../utils/PersistedDataGridPro";
import { CustomFooterPersonalized } from "../utils/CustomFooterPersonalized";

const protocolFields = [
  ["Cadenze", "Cadenza (mesi)"],
  ["LineeGuida", "Linee guida"],
  ["WCodePersonalizzato", "Codice personalizzato"],
  ["Preassuntiva", "Preventiva"],
  ["Extra", "Extra"],
  ["Pacchetto", "Pacchetto"],
];
function protocolValue(field, value) {
  if (value === null || value === undefined) return "Non impostato";
  if (["Preassuntiva", "Extra", "Pacchetto"].includes(field))
    return Number(value) === 1 ? "Sì" : "No";
  return value === "" ? "Vuoto" : String(value);
}
function ProtocolComparison({ accertamenti, origine, destinazione }) {
  if (!accertamenti?.length)
    return (
      <Typography variant="body2" sx={{ mt: 1 }}>
        Nessun accertamento in entrambi i protocolli.
      </Typography>
    );
  return (
    <TableContainer sx={{ mt: 2 }}>
      <Table size="small" aria-label="Confronto protocolli sanitari">
        <TableHead>
          <TableRow>
            <TableCell>Accertamento / impostazione</TableCell>
            <TableCell>{origine}</TableCell>
            <TableCell>{destinazione}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accertamenti.map((exam) => (
            <React.Fragment key={exam.idAccertamento || exam.accertamento}>
              <TableRow
                sx={{ bgcolor: exam.differenze.length ? "#fff4e5" : "#eef2f6" }}
              >
                <TableCell sx={{ fontWeight: 600 }}>
                  {exam.accertamento}
                </TableCell>
                <TableCell>{exam.origine ? "Presente" : "Assente"}</TableCell>
                <TableCell>
                  {exam.destinazione ? "Presente" : "Assente"}
                </TableCell>
              </TableRow>
              {protocolFields.map(([field, label]) => (
                <TableRow
                  key={field}
                  sx={{
                    bgcolor: exam.differenze.includes(field)
                      ? "#fff4e5"
                      : undefined,
                  }}
                >
                  <TableCell sx={{ pl: 3 }}>{label}</TableCell>
                  <TableCell
                    sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                  >
                    {exam.origine
                      ? protocolValue(field, exam.origine[field])
                      : "—"}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                  >
                    {exam.destinazione
                      ? protocolValue(field, exam.destinazione[field])
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom timeout={2000} in={true} ref={ref} {...props} />;
});

export const AnagraficaMansioniMedicina = () => {
  const [anteprimaUnione, setAnteprimaUnione] = useState(null);
  const [aziendeConfermate, setAziendeConfermate] = useState([]);
  const [origineUnione, setOrigineUnione] = useState(null);
  const [destinazioneUnione, setDestinazioneUnione] = useState(null);
  const [unioneInCorso, setUnioneInCorso] = useState(false);
  const [erroreUnione, setErroreUnione] = useState("");
  const [mansioni, setMansioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mansioneSelezionata, setMansioneSelezionata] = useState([]);
  const [openSnack, setOpenSnack] = useState(false);
  const [snack, setSnack] = useState({
    severity: "success",
    messaggio: "Salvataggio Effettuato correttamente",
  });

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const reloadData = async () => {
    let response = await unsplash.get("anagraficaMansioniMedicina", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      },
    });

    setMansioni(response.data.data);
  };

  const caricaAnteprima = async () => {
    setUnioneInCorso(true);
    setErroreUnione("");
    try {
      const response = await unsplash.post(
        "anagraficaMansioniMedicina/unisci/anteprima",
        {
          origine: origineUnione.ID,
          destinazione: destinazioneUnione.ID,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` } },
      );
      setAnteprimaUnione(response.data);
      setAziendeConfermate([]);
    } catch (error) {
      setErroreUnione(
        error.response?.data?.message || "Impossibile caricare l'anteprima",
      );
    } finally {
      setUnioneInCorso(false);
    }
  };

  const unisci = async () => {
    setUnioneInCorso(true);
    setErroreUnione("");
    try {
      const response = await unsplash.post(
        "anagraficaMansioniMedicina/unisci",
        {
          origine: origineUnione.ID,
          destinazione: destinazioneUnione.ID,
          fingerprint: anteprimaUnione.fingerprint,
          aziendeConfermate,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` } },
      );
      setOrigineUnione(null);
      setDestinazioneUnione(null);
      setSnack({
        severity: "success",
        messaggio: response.data.message || "Aziende selezionate trasferite",
      });
      setOpenSnack(true);
      await reloadData();
    } catch (error) {
      setErroreUnione(
        error.response?.data?.message || "Impossibile unire le mansioni",
      );
    } finally {
      setUnioneInCorso(false);
    }
  };

  const handleSave = async (mansione, operazione) => {
    if (!mansione?.Descrizione || mansione.Descrizione.trim() === "") {
      setSnack({
        severity: "error",
        messaggio: "Descrizione obbligatoria",
      });
      setOpenSnack(true);
      return;
    }

    if (operazione === "new") {
      const esistente = mansioni.find(
        (el) =>
          el.Descrizione?.trim().toLowerCase() ===
          mansione.Descrizione?.trim().toLowerCase(),
      );
      if (esistente) {
        setSnack({
          severity: "error",
          messaggio: "Mansione già esistente",
        });
        setOpenSnack(true);
        return;
      }

      try {
        await unsplash.post(
          "anagraficaMansioniMedicina",
          { mansione: { Descrizione: mansione.Descrizione.trim() } },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          },
        );
        setSnack({
          severity: "success",
          messaggio: "Mansione medicina creata con successo",
        });
        setOpenSnack(true);
        reloadData();
      } catch {
        setSnack({
          severity: "error",
          messaggio: "Errore durante la creazione",
        });
        setOpenSnack(true);
      }
      return;
    }

    try {
      await unsplash.put(
        "anagraficaMansioniMedicina",
        { mansione: { ID: mansione.ID, Descrizione: mansione.Descrizione } },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        },
      );
      setSnack({
        severity: "success",
        messaggio: "Mansione medicina aggiornata con successo",
      });
      setOpenSnack(true);
      reloadData();
    } catch {
      setSnack({
        severity: "error",
        messaggio: "Errore durante l'aggiornamento",
      });
      setOpenSnack(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await unsplash.delete("anagraficaMansioniMedicina", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        data: { id },
      });

      if (response?.data?.blocked) {
        setSnack({
          severity: "warning",
          messaggio: response?.data?.message || "Mansione non eliminabile",
        });
      } else {
        setSnack({
          severity: "success",
          messaggio: "Mansione medicina eliminata con successo",
        });
      }
      setOpenSnack(true);
      reloadData();
    } catch {
      setSnack({
        severity: "error",
        messaggio: "Errore durante l'eliminazione",
      });
      setOpenSnack(true);
    }
  };

  const handleDownloadEsplosoExcel = async () => {
    try {
      const response = await unsplash.get("anagraficaMansioniMedicina", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        params: { esploso: true },
      });

      const rows = (response?.data?.data || []).map((el) => ({
        ID_Mansione: el.IDMansione,
        Mansione_Medicina: el.MansioneMedicina,
        ID_Societa: el.IDCliente || "",
        Ragione_Sociale: el.RagioneSociale || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Esploso");
      XLSX.writeFile(workbook, "anagrafica_mansioni_medicina_esploso.xlsx");
    } catch {
      setSnack({
        severity: "error",
        messaggio: "Errore durante il download dell'esploso Excel",
      });
      setOpenSnack(true);
    }
  };

  const columns = [
    {
      filterable: false,
      field: "Actions",
      headerName: "Azioni",
      width: 175,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <IconButton
            aria-label="edit"
            onClick={async () => {
              await setMansioneSelezionata(params.row);
              document.getElementById("editMansioneMedicina").click();
            }}
          >
            <EditIcon
              style={{ height: "2rem", width: "2rem", color: "#2265B1" }}
            />
          </IconButton>
          <DeleteDialog mansione={params.row} onConfirm={handleDelete} />
          <Tooltip title="Unisci a un'altra mansione">
            <IconButton
              aria-label="Unisci mansione"
              onClick={() => {
                setAnteprimaUnione(null);
                setAziendeConfermate([]);
                setOrigineUnione(params.row);
                setDestinazioneUnione(null);
                setErroreUnione("");
              }}
            >
              <MergeTypeIcon sx={{ color: "#11526F" }} />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
    {
      field: "Descrizione",
      headerName: "Descrizione",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "NumeroSocieta",
      headerName: "N. Societa Associate",
      width: 220,
      align: "center",
      headerAlign: "center",
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <GridToolbarQuickFilter placeholder="Cerca..." />
        </Grid>
      </GridToolbarContainer>
    );
  }

  useEffect(() => {
    (async () => {
      try {
        await reloadData();
        setLoading(false);
      } catch (error) {
        console.error(error.message);
      }
    })();
  }, []);

  return (
    <>
      <Dialog
        open={Boolean(origineUnione)}
        onClose={() => {
          if (!unioneInCorso) setOrigineUnione(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Unisci mansione medicina</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Saranno trasferiti solo i collegamenti delle aziende selezionate da
            «{origineUnione?.Descrizione}» alla mansione di destinazione. La
            mansione originale resterà per le aziende escluse e sarà archiviata
            quando non avrà più collegamenti attuali. Operazione riservata agli
            amministratori.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Se la destinazione non ha un protocollo per l’azienda, verrà copiato
            quello originale. Se ha già un protocollo, i due devono essere
            identici: stessi accertamenti, cadenze, linee guida, codici
            personalizzati, preventiva, extra e pacchetti.
          </Typography>
          <Autocomplete
            options={mansioni.filter((m) => m.ID !== origineUnione?.ID)}
            value={destinazioneUnione}
            onChange={(_, value) => setDestinazioneUnione(value)}
            disabled={unioneInCorso || Boolean(anteprimaUnione)}
            getOptionLabel={(m) => `${m.Descrizione} (ID ${m.ID})`}
            isOptionEqualToValue={(a, b) => a.ID === b.ID}
            renderInput={(params) => (
              <TextField {...params} label="Mansione di destinazione" />
            )}
          />
          {anteprimaUnione && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">
                Cosa verrà fatto per ogni azienda
              </Typography>
              <Typography sx={{ mb: 2 }}>
                «{anteprimaUnione.origine.Descrizione}» → «
                {anteprimaUnione.destinazione.Descrizione}». Seleziona le
                aziende da trasferire. Le altre resteranno invariate.
              </Typography>
              {anteprimaUnione.aziende.map((azienda) => (
                <Box
                  key={azienda.ID}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    p: 2,
                    mb: 1,
                  }}
                >
                  <FormControlLabel
                    label={azienda.RagioneSociale}
                    control={
                      <Checkbox
                        disabled={unioneInCorso || Boolean(azienda.conflitto)}
                        checked={aziendeConfermate.includes(azienda.ID)}
                        onChange={(_, checked) =>
                          setAziendeConfermate((ids) =>
                            checked
                              ? [...ids, azienda.ID]
                              : ids.filter((id) => id !== azienda.ID),
                          )
                        }
                      />
                    }
                  />
                  {azienda.conflitto && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {azienda.conflitto}
                    </Alert>
                  )}
                  {azienda.copiaProtocollo && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      La destinazione non ha un protocollo: saranno copiati
                      tutti gli accertamenti e le impostazioni originali. Il
                      protocollo delle visite già effettuate sarà conservato.
                    </Alert>
                  )}
                  {azienda.conflitto && (
                    <ProtocolComparison
                      accertamenti={azienda.accertamenti}
                      origine={anteprimaUnione.origine.Descrizione}
                      destinazione={anteprimaUnione.destinazione.Descrizione}
                    />
                  )}
                  <Typography variant="body2">
                    {azienda.DestinazioneGiaAssociata
                      ? "La destinazione è già associata: i collegamenti saranno riuniti."
                      : "La mansione di destinazione sarà associata all'azienda."}
                  </Typography>
                  <Typography variant="body2">
                    Da trasferire: {azienda.Lavoratori} lavoratori,{" "}
                    {azienda.Visite} visite future, {azienda.Protocolli} righe
                    di protocollo, {azienda.Rischi} rischi, {azienda.Distacchi}{" "}
                    collegamenti di distacco e {azienda.CollegamentiSicurezza}{" "}
                    associazioni sicurezza. Le {azienda.VisiteStoriche || 0}{" "}
                    visite già effettuate manterranno la mansione originale.
                  </Typography>
                </Box>
              ))}
              {anteprimaUnione.aziende.length === 0 && (
                <Typography>Nessuna azienda attiva coinvolta.</Typography>
              )}
              {anteprimaUnione.aziendeCessate > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Le {anteprimaUnione.aziendeCessate} aziende cessate non sono
                  mostrate e non saranno modificate. I loro collegamenti
                  manterranno in vita la mansione originale.
                </Alert>
              )}
              {anteprimaUnione.collegamentiGlobali > 0 && (
                <Typography sx={{ mt: 1 }}>
                  I {anteprimaUnione.collegamentiGlobali} collegamenti globali
                  sicurezza–medicina rimarranno sulla mansione originale fino al
                  trasferimento di tutti i collegamenti aziendali.
                </Typography>
              )}
            </Box>
          )}
          {erroreUnione && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {erroreUnione}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {anteprimaUnione && (
            <Button
              disabled={unioneInCorso}
              onClick={() => {
                setAnteprimaUnione(null);
                setAziendeConfermate([]);
              }}
            >
              Indietro
            </Button>
          )}
          <Button
            disabled={unioneInCorso}
            onClick={() => setOrigineUnione(null)}
          >
            Annulla
          </Button>
          <Button
            disabled={
              !destinazioneUnione ||
              unioneInCorso ||
              (Boolean(anteprimaUnione) && aziendeConfermate.length === 0)
            }
            variant="contained"
            onClick={anteprimaUnione ? unisci : caricaAnteprima}
          >
            {unioneInCorso
              ? "Attendere..."
              : anteprimaUnione
                ? "Trasferisci aziende selezionate"
                : "Mostra anteprima"}
          </Button>
        </DialogActions>
      </Dialog>
      <GlobalSnackbar
        severity={snack.severity}
        messaggio={snack.messaggio}
        openSnack={openSnack}
        handleCloseSnack={handleCloseSnack}
      />
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: { xs: 0 } }}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Grid item xs={12} md={4}>
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <Button
                          startIcon={<Download />}
                          onClick={handleDownloadEsplosoExcel}
                          sx={{ color: "white", mr: 1 }}
                          variant="outlined"
                          className="ButtonMiller"
                        >
                          Esploso
                        </Button>
                        <MansioneDialog
                          id="editMansioneMedicina"
                          mansione={mansioneSelezionata}
                          saveFunction={handleSave}
                          title="Modifica Mansione Medicina"
                        />
                        <MansioneDialog
                          id="newMansioneMedicina"
                          mansione={mansioneSelezionata}
                          saveFunction={handleSave}
                          title="Nuova Mansione Medicina"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      height: { xs: "75vh", md: "60vh", xl: "75vh" },
                      width: 1,
                    }}
                  >
                    <DataGridPro
                      initialState={{
                        pinnedColumns: { left: ["Actions"] },
                      }}
                      components={{
                        Toolbar: CustomToolbar,
                        Footer: CustomFooterPersonalized,
                      }}
                      slots={{
                        loadingOverlay: LinearProgress,
                      }}
                      loading={loading}
                      localeText={
                        itIT.components.MuiDataGrid.defaultProps.localeText
                      }
                      rowHeight={52}
                      rows={mansioni}
                      columns={columns}
                      getRowId={(row) => row.ID}
                      pageSize={20}
                      rowsPerPageOptions={[20]}
                      disableSelectionOnClick
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};

const MansioneDialog = (props) => {
  const [open, setOpen] = useState(false);
  const [mansione, setMansione] = useState({
    ID: null,
    Descrizione: "",
  });
  const [societaAssociate, setSocietaAssociate] = useState([]);
  const [loadingSocietaAssociate, setLoadingSocietaAssociate] = useState(false);

  const handleClickOpen = () => {
    if (props.id === "editMansioneMedicina") {
      setMansione(props.mansione);
      loadSocietaAssociate(props.mansione?.ID);
    } else {
      setMansione({
        ID: null,
        Descrizione: "",
      });
      setSocietaAssociate([]);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    await props.saveFunction(
      mansione,
      props.id === "editMansioneMedicina" ? "edit" : "new",
    );
    handleClose();
  };

  const loadSocietaAssociate = async (idMansione) => {
    if (!idMansione) {
      setSocietaAssociate([]);
      return;
    }
    setLoadingSocietaAssociate(true);
    try {
      const response = await unsplash.get("anagraficaMansioniMedicina", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        params: { idMansione },
      });
      setSocietaAssociate(response?.data?.data || []);
    } catch {
      setSocietaAssociate([]);
    }
    setLoadingSocietaAssociate(false);
  };

  return (
    <>
      <ButtonGroup variant="contained">
        {props.id === "editMansioneMedicina" ? (
          <Button
            startIcon={<Add />}
            onClick={handleClickOpen}
            id={props.id}
            sx={{ color: "white", display: "none" }}
            variant="outlined"
            className="ButtonMiller"
          >
            Aggiungi
          </Button>
        ) : (
          <Button
            startIcon={<Add />}
            onClick={handleClickOpen}
            id={props.id}
            sx={{ color: "white" }}
            variant="outlined"
            className="ButtonMiller"
          >
            Aggiungi
          </Button>
        )}
      </ButtonGroup>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="sm"
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ mx: "auto" }}>{props.title}</DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item xs={12}>
              <TextField
                value={mansione?.Descrizione || ""}
                label="Descrizione"
                name="Descrizione"
                onChange={(e) =>
                  setMansione({ ...mansione, Descrizione: e.target.value })
                }
                fullWidth
                sx={{ mt: 2 }}
              />
            </Grid>
            {props.id === "editMansioneMedicina" ? (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Societa associate ({societaAssociate.length})
                </Typography>
                {loadingSocietaAssociate ? (
                  <LinearProgress sx={{ mt: 1 }} />
                ) : societaAssociate.length > 0 ? (
                  <List dense sx={{ maxHeight: 220, overflowY: "auto", mt: 1 }}>
                    {societaAssociate.map((societa) => (
                      <ListItem key={societa.IDCliente} sx={{ py: 0 }}>
                        <ListItemText primary={societa.RagioneSociale} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Nessuna societa associata.
                  </Typography>
                )}
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mx: 2,
          }}
        >
          <Button
            onClick={handleClose}
            variant="contained"
            className="ButtonMiller"
            startIcon={<Close />}
          >
            Chiudi
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className="ButtonMiller"
            startIcon={<Check />}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DeleteDialog = (props) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton aria-label="delete" onClick={() => setOpen(true)}>
        <Delete style={{ height: "2rem", width: "2rem", color: "#FF2768" }} />
      </IconButton>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ mx: "auto" }}>
          Sei sicuro di voler eliminare la mansione selezionata?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Attenzione, sei sicuro di voler eliminare la seguente mansione:
            <br />
            <b>{props.mansione.Descrizione}</b>
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 3,
          }}
        >
          <Button onClick={() => setOpen(false)}>annulla</Button>
          <Button
            onClick={() => {
              props.onConfirm(props.mansione.ID);
              setOpen(false);
            }}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
